import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  Typography,
  CircularProgress,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import SiteDocumentationService from "@/services/siteDocumentation";
import "@/styles/modal.scss";

interface DeleteSiteDocumentationConfirmProps {
  open: boolean;
  documents: Array<{ id: string; name: string }>;
  onClose: () => void;
  onSuccess: (count: number) => void;
}

const DeleteSiteDocumentationConfirm: React.FC<
  DeleteSiteDocumentationConfirmProps
> = ({ open, documents, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log("Deleting documents:", documents);
      const siteDocService = new SiteDocumentationService();

      // Delete all documents in parallel using service
      await Promise.all(
        documents.map((doc) => {
          console.log(`Deleting document ID: ${doc.id}`);
          return siteDocService.deleteDocumentation(doc.id);
        })
      );

      console.log("Documents deleted successfully");
      setLoading(false);
      onSuccess(documents.length);
      onClose();
    } catch (err) {
      console.error("Delete API error:", err);
      setError(
        t(
          "deleteDocumentationFailed",
          "Failed to delete document(s). Please try again."
        )
      );
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && documents.length === 0) {
      onClose();
    }
  }, [open, documents]);

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          className: "modal-dialog",
        },
      }}
    >
      <Box className="modal-header">
        <DialogTitle className="modal-header-title">
          {t("deleteDocumentationTitle", "DELETE SITE DOCUMENTATION")}
        </DialogTitle>
        <IconButton
          onClick={handleClose}
          size="small"
          className="modal-close-button"
          aria-label="close"
          disabled={loading}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <DialogContent className="modal-content-padded">
        <Box>
          <Typography
            variant="body1"
            sx={{
              color: "#4a5568",
              fontWeight: 500,
              mb: 1,
            }}
          >
            {documents.length > 1
              ? t(
                  "deleteDocumentsConfirmMessage",
                  `Are you sure you want to delete these ${documents.length} documents?`
                )
              : t(
                  "deleteDocumentConfirmMessage",
                  "Are you sure you want to delete this document?"
                )}
          </Typography>
          <Box
            component="ul"
            sx={{
              fontSize: "0.95rem",
              color: "#718096",
              lineHeight: 1.8,
              fontWeight: 500,
              mb: 2,
              pl: 2.5,
              maxHeight: "200px",
              overflowY: "auto",
            }}
          >
            {documents.map((doc, index) => (
              <li key={index}>{doc.name}</li>
            ))}
          </Box>
          <Typography
            variant="body2"
            sx={{
              color: "#e53e3e",
              lineHeight: 1.5,
            }}
          >
            {t("deleteDocumentationWarning", "This action cannot be undone.")}
          </Typography>
        </Box>
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions className="modal-actions-flex">
        <Button
          variant="outlined"
          fullWidth
          onClick={handleClose}
          disabled={loading}
          className="modal-button-base modal-button-outlined"
        >
          {t("cancel", "CANCEL")}
        </Button>
        <Button
          variant="contained"
          fullWidth
          onClick={handleConfirm}
          className="modal-button-base modal-button-primary"
          disabled={loading}
        >
          {loading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            t("delete", "DELETE")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteSiteDocumentationConfirm;
